// Mock data for dashboard testing without API calls
export interface MockDashboardInsights {
  personalizedGreeting: string;
  nextBestAction: {
    id: string;
    title: string;
    description: string;
    actionType: string;
    actionId?: string;
    priority: string;
    confidence: number;
    estimatedTime: string;
    difficulty: 'easy' | 'medium' | 'hard';
    category: string;
    type: 'recipe' | 'lesson' | 'quiz' | 'video';
    estimatedMinutes: number;
    reasoning: string;
  };
  progressInsights: Array<{
    id: string;
    type: 'achievement' | 'improvement' | 'challenge' | 'milestone';
    title: string;
    description: string;
    metric: string;
    value: number;
    change: number;
    trend: 'up' | 'down' | 'stable';
    aiGenerated: boolean;
  }>;
  motivationalMessage: string;
  adaptationSuggestions: Array<{
    id: string;
    title: string;
    description: string;
    priority: number;
  }>;
  streakInfo: {
    current: number;
    longest: number;
    target: number;
    encouragement: string;
    lastActivity: string;
  };
  upcomingMilestones: Array<{
    id: string;
    title: string;
    description: string;
    progress: number;
    target: number;
  }>;
  recentActivity: Array<{
    id: string;
    type: 'lesson' | 'quiz' | 'achievement' | 'recipe';
    title: string;
    timestamp: string;
    score?: number;
  }>;
  recommendations: Array<{
    id: string;
    title: string;
    description: string;
    type: 'recipe' | 'lesson' | 'quiz' | 'video';
    difficulty: 'easy' | 'medium' | 'hard';
    estimatedTime: string;
    rating: number;
    category: string;
    confidence: number;
  }>;
}

export interface MockRecommendation {
  id: string;
  userId: string;
  contentId: string;
  contentType: 'recipe' | 'lesson' | 'quiz' | 'video';
  title: string;
  description: string;
  thumbnailUrl?: string;
  difficultyLevel: 'beginner' | 'intermediate' | 'advanced' | 'expert';
  estimatedMinutes: number;
  rating: number;
  category: string;
  tags: string[];
  confidence: number;
  reasoning: {
    primaryReason: string;
    confidence: number;
    skillAlignment: Array<{
      skillId: string;
      skillName: string;
      alignmentScore: number;
      gapFilled: boolean;
      masteryImpact: number;
    }>;
    difficultyFit: number;
    personalizedFactors: string[];
    alternativeOptions: string[];
  };
  metadata: Record<string, any>;
  priorityScore?: number;
  priorityReasons?: string[];
}

// Generate personalized greeting based on time of day and user context
export function generatePersonalizedGreeting(
  skillLevel: string,
  journeyStage: string,
  timeOfDay: 'morning' | 'afternoon' | 'evening' = 'morning'
): string {
  const timeGreetings = {
    morning: 'Good morning',
    afternoon: 'Good afternoon',
    evening: 'Good evening',
  };

  const skillGreetings = {
    beginner: 'Welcome to your coffee journey!',
    intermediate: 'Ready to level up your coffee skills?',
    advanced: 'Time to master new techniques!',
    expert: "Let's explore advanced coffee artistry!",
  };

  const journeyGreetings = {
    onboarding: "Let's start with the basics.",
    learning: 'Keep building your knowledge!',
    practicing: 'Time to put theory into practice!',
    mastering: "You're becoming a true coffee expert!",
  };

  return `${timeGreetings[timeOfDay]}! ${skillGreetings[skillLevel as keyof typeof skillGreetings]} ${journeyGreetings[journeyStage as keyof typeof journeyGreetings]}`;
}

// Generate mock insights based on user context
export function generateMockInsights(
  userId: string,
  skillLevel: string,
  journeyStage: string
): MockDashboardInsights {
  const timeOfDay = getTimeOfDay();

  return {
    personalizedGreeting: generatePersonalizedGreeting(
      skillLevel,
      journeyStage,
      timeOfDay
    ),
    nextBestAction: {
      id: `action_${Date.now()}`,
      title: getNextBestActionTitle(skillLevel, journeyStage),
      description: getNextBestActionDescription(skillLevel, journeyStage),
      actionType: getNextBestActionType(skillLevel, journeyStage),
      actionId: `content_${Date.now()}`,
      priority: 'high',
      confidence: 0.85,
      estimatedTime: getEstimatedTime(skillLevel),
      difficulty: getDifficulty(skillLevel),
      category: getCategory(skillLevel, journeyStage),
      type: 'lesson' as const,
      estimatedMinutes: getEstimatedMinutes(skillLevel),
      reasoning: `Perfect for ${skillLevel} level users in ${journeyStage} stage`,
    },
    progressInsights: generateProgressInsights(skillLevel, journeyStage),
    motivationalMessage: getMotivationalMessage(skillLevel, journeyStage),
    adaptationSuggestions: getAdaptationSuggestions(skillLevel, journeyStage),
    streakInfo: {
      current: getCurrentStreak(skillLevel),
      longest: getLongestStreak(skillLevel),
      target: 30,
      encouragement: getStreakEncouragement(skillLevel),
      lastActivity: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    },
    upcomingMilestones: getUpcomingMilestones(skillLevel, journeyStage),
    recentActivity: generateRecentActivity(skillLevel),
    recommendations: generateRecommendations(skillLevel, journeyStage),
  };
}

// Generate mock recommendations
export function generateMockRecommendations(
  skillLevel: string,
  journeyStage: string,
  limit: number = 10
): MockRecommendation[] {
  const baseRecommendations = [
    {
      id: 'rec_1',
      userId: 'test-user-123',
      title: 'Perfect Espresso Extraction',
      description:
        'Master the art of pulling the perfect shot with proper timing and pressure.',
      contentType: 'recipe' as const,
      contentId: 'recipe_espresso_perfect',
      difficultyLevel: 'intermediate' as const,
      estimatedMinutes: 15,
      rating: 4.8,
      category: 'espresso',
      tags: ['espresso', 'brewing', 'technique'],
      confidence: 0.9,
      reasoning: {
        primaryReason:
          'This recipe perfectly matches your intermediate skill level and fills a key gap in your espresso technique knowledge.',
        confidence: 0.9,
        skillAlignment: [
          {
            skillId: 'espresso-basics',
            skillName: 'Espresso Basics',
            alignmentScore: 0.85,
            gapFilled: true,
            masteryImpact: 0.8,
          },
          {
            skillId: 'pressure-control',
            skillName: 'Pressure Control',
            alignmentScore: 0.92,
            gapFilled: false,
            masteryImpact: 0.7,
          },
        ],
        difficultyFit: 0.88,
        personalizedFactors: [
          'Matches your preferred brewing time',
          'Builds on your existing knowledge',
          'High community rating',
        ],
        alternativeOptions: [
          'Espresso Troubleshooting Guide',
          'Advanced Espresso Techniques',
        ],
      },
      metadata: { difficulty: 'medium', timeRequired: '15 min' },
    },
    {
      id: 'rec_2',
      userId: 'test-user-123',
      title: 'Latte Art Basics',
      description:
        'Learn fundamental latte art techniques for beautiful coffee presentation.',
      contentType: 'lesson' as const,
      contentId: 'lesson_latte_art_basics',
      difficultyLevel: 'beginner' as const,
      estimatedMinutes: 30,
      rating: 4.6,
      category: 'latte_art',
      tags: ['latte_art', 'presentation', 'milk'],
      confidence: 0.85,
      reasoning: {
        primaryReason:
          'Perfect introduction to latte art that builds on your current milk steaming skills.',
        confidence: 0.85,
        skillAlignment: [
          {
            skillId: 'milk-steaming',
            skillName: 'Milk Steaming',
            alignmentScore: 0.78,
            gapFilled: false,
            masteryImpact: 0.6,
          },
          {
            skillId: 'presentation',
            skillName: 'Coffee Presentation',
            alignmentScore: 0.65,
            gapFilled: true,
            masteryImpact: 0.9,
          },
        ],
        difficultyFit: 0.82,
        personalizedFactors: [
          'Great for beginners',
          'Builds presentation skills',
          'Popular with your skill level',
        ],
        alternativeOptions: [
          'Advanced Latte Art Techniques',
          'Coffee Photography Basics',
        ],
      },
      metadata: { difficulty: 'easy', timeRequired: '30 min' },
    },
    {
      id: 'rec_3',
      userId: 'test-user-123',
      title: 'Coffee Bean Roasting Guide',
      description:
        'Understand the roasting process and how it affects flavor profiles.',
      contentType: 'video' as const,
      contentId: 'video_roasting_guide',
      difficultyLevel: 'advanced' as const,
      estimatedMinutes: 45,
      rating: 4.9,
      category: 'roasting',
      tags: ['roasting', 'beans', 'flavor'],
      confidence: 0.95,
      reasoning: {
        primaryReason:
          'Advanced content that will challenge you and expand your understanding of coffee science.',
        confidence: 0.95,
        skillAlignment: [
          {
            skillId: 'flavor-analysis',
            skillName: 'Flavor Analysis',
            alignmentScore: 0.88,
            gapFilled: false,
            masteryImpact: 0.85,
          },
          {
            skillId: 'coffee-science',
            skillName: 'Coffee Science',
            alignmentScore: 0.92,
            gapFilled: true,
            masteryImpact: 0.9,
          },
        ],
        difficultyFit: 0.91,
        personalizedFactors: [
          'Matches your advanced level',
          'Highly rated by experts',
          'Comprehensive coverage',
        ],
        alternativeOptions: [
          'Home Roasting Equipment Guide',
          'Roasting Profile Development',
        ],
      },
      metadata: { difficulty: 'hard', timeRequired: '45 min' },
    },
    {
      id: 'rec_4',
      userId: 'test-user-123',
      title: 'French Press Mastery',
      description:
        'Perfect your French press technique for rich, full-bodied coffee.',
      contentType: 'recipe' as const,
      contentId: 'recipe_french_press',
      difficultyLevel: 'beginner' as const,
      estimatedMinutes: 10,
      rating: 4.7,
      category: 'brewing',
      tags: ['french_press', 'brewing', 'full_bodied'],
      confidence: 0.8,
      reasoning: {
        primaryReason:
          'Excellent starting point for brewing fundamentals with immediate results.',
        confidence: 0.8,
        skillAlignment: [
          {
            skillId: 'brewing-basics',
            skillName: 'Brewing Basics',
            alignmentScore: 0.95,
            gapFilled: true,
            masteryImpact: 0.8,
          },
          {
            skillId: 'water-temperature',
            skillName: 'Water Temperature Control',
            alignmentScore: 0.76,
            gapFilled: false,
            masteryImpact: 0.7,
          },
        ],
        difficultyFit: 0.85,
        personalizedFactors: [
          'Quick and easy to master',
          'Great for beginners',
          'Immediate satisfaction',
        ],
        alternativeOptions: ['Pour Over Basics', 'Aeropress Techniques'],
      },
      metadata: { difficulty: 'easy', timeRequired: '10 min' },
    },
    {
      id: 'rec_5',
      userId: 'test-user-123',
      title: 'Coffee Tasting Fundamentals',
      description:
        'Develop your palate and learn to identify different flavor notes.',
      contentType: 'quiz' as const,
      contentId: 'quiz_tasting_fundamentals',
      difficultyLevel: 'intermediate' as const,
      estimatedMinutes: 20,
      rating: 4.5,
      category: 'tasting',
      tags: ['tasting', 'flavor', 'palate'],
      confidence: 0.75,
      reasoning: {
        primaryReason:
          'Builds essential tasting skills that will enhance your overall coffee experience.',
        confidence: 0.75,
        skillAlignment: [
          {
            skillId: 'flavor-identification',
            skillName: 'Flavor Identification',
            alignmentScore: 0.68,
            gapFilled: true,
            masteryImpact: 0.85,
          },
          {
            skillId: 'sensory-development',
            skillName: 'Sensory Development',
            alignmentScore: 0.72,
            gapFilled: false,
            masteryImpact: 0.8,
          },
        ],
        difficultyFit: 0.78,
        personalizedFactors: [
          'Interactive learning format',
          'Builds tasting confidence',
          'Good for intermediate level',
        ],
        alternativeOptions: [
          'Advanced Cupping Techniques',
          'Flavor Wheel Mastery',
        ],
      },
      metadata: { difficulty: 'medium', timeRequired: '20 min' },
    },
  ];

  // Filter and prioritize based on skill level and journey stage
  let filtered = baseRecommendations.filter(rec => {
    if (skillLevel === 'beginner' && rec.difficultyLevel === 'advanced')
      return false;
    if (skillLevel === 'expert' && rec.difficultyLevel === 'beginner')
      return false;
    return true;
  });

  // Add priority scores based on user context
  filtered = filtered.map(rec => ({
    ...rec,
    priorityScore: calculatePriorityScore(rec, skillLevel, journeyStage),
    priorityReasons: generatePriorityReasons(rec, skillLevel, journeyStage),
  }));

  // Sort by priority score and return limited results
  return filtered
    .sort((a, b) => (b.priorityScore || 0) - (a.priorityScore || 0))
    .slice(0, limit);
}

// Helper functions
function getTimeOfDay(): 'morning' | 'afternoon' | 'evening' {
  const hour = new Date().getHours();
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
}

function getNextBestActionTitle(
  skillLevel: string,
  journeyStage: string
): string {
  const titles = {
    beginner: {
      onboarding: 'Start Your First Lesson',
      learning: 'Complete Basic Brewing',
      practicing: 'Try Your First Recipe',
      mastering: 'Master the Fundamentals',
    },
    intermediate: {
      onboarding: 'Explore Advanced Techniques',
      learning: 'Learn Latte Art',
      practicing: 'Perfect Your Espresso',
      mastering: 'Try Advanced Recipes',
    },
    advanced: {
      onboarding: 'Master Professional Techniques',
      learning: 'Study Coffee Science',
      practicing: 'Create Your Own Recipes',
      mastering: 'Teach Others',
    },
    expert: {
      onboarding: 'Explore Coffee Innovation',
      learning: 'Research New Methods',
      practicing: 'Mentor Beginners',
      mastering: 'Lead Coffee Education',
    },
  };

  return (
    titles[skillLevel as keyof typeof titles]?.[
      journeyStage as keyof (typeof titles)[keyof typeof titles]
    ] || 'Continue Learning'
  );
}

function getNextBestActionDescription(
  skillLevel: string,
  journeyStage: string
): string {
  return `Based on your ${skillLevel} level and ${journeyStage} stage, this is the perfect next step in your coffee journey.`;
}

function getNextBestActionType(
  skillLevel: string,
  journeyStage: string
): string {
  if (skillLevel === 'beginner' && journeyStage === 'onboarding')
    return 'continue_journey';
  if (journeyStage === 'practicing') return 'try_recipe';
  return 'browse_recipes';
}

function getEstimatedTime(skillLevel: string): string {
  const times = {
    beginner: '10-15 min',
    intermediate: '20-30 min',
    advanced: '30-45 min',
    expert: '45-60 min',
  };
  return times[skillLevel as keyof typeof times] || '15-20 min';
}

function getEstimatedMinutes(skillLevel: string): number {
  const minutes = {
    beginner: 12,
    intermediate: 25,
    advanced: 37,
    expert: 52,
  };
  return minutes[skillLevel as keyof typeof minutes] || 18;
}

function getDifficulty(skillLevel: string): 'easy' | 'medium' | 'hard' {
  const difficulties = {
    beginner: 'easy',
    intermediate: 'medium',
    advanced: 'hard',
    expert: 'hard',
  };
  return difficulties[skillLevel as keyof typeof difficulties] || 'medium';
}

function getCategory(skillLevel: string, journeyStage: string): string {
  const categories = {
    beginner: 'fundamentals',
    intermediate: 'techniques',
    advanced: 'mastery',
    expert: 'innovation',
  };
  return categories[skillLevel as keyof typeof categories] || 'general';
}

function getTotalLessons(skillLevel: string): number {
  const totals = { beginner: 20, intermediate: 35, advanced: 50, expert: 75 };
  return totals[skillLevel as keyof typeof totals] || 25;
}

function getCompletedLessons(skillLevel: string): number {
  const completed = { beginner: 3, intermediate: 12, advanced: 28, expert: 45 };
  return completed[skillLevel as keyof typeof completed] || 5;
}

function getCurrentStreak(skillLevel: string): number {
  const streaks = { beginner: 2, intermediate: 7, advanced: 15, expert: 30 };
  return streaks[skillLevel as keyof typeof streaks] || 1;
}

function getLongestStreak(skillLevel: string): number {
  const longest = { beginner: 5, intermediate: 12, advanced: 25, expert: 45 };
  return longest[skillLevel as keyof typeof longest] || 3;
}

function getAchievementsUnlocked(skillLevel: string): number {
  const achievements = {
    beginner: 2,
    intermediate: 8,
    advanced: 18,
    expert: 35,
  };
  return achievements[skillLevel as keyof typeof achievements] || 1;
}

function getTotalAchievements(skillLevel: string): number {
  const totals = { beginner: 10, intermediate: 25, advanced: 40, expert: 60 };
  return totals[skillLevel as keyof typeof totals] || 15;
}

function getNextMilestone(skillLevel: string, journeyStage: string): string {
  const milestones = {
    beginner: 'Complete 5 lessons',
    intermediate: 'Master 3 brewing methods',
    advanced: 'Create your first recipe',
    expert: 'Mentor a beginner',
  };
  return (
    milestones[skillLevel as keyof typeof milestones] || 'Continue learning'
  );
}

function generateRecentActivity(skillLevel: string) {
  const activities = [
    {
      id: 'act_1',
      type: 'lesson' as const,
      title: 'Espresso Basics',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      score: 85,
    },
    {
      id: 'act_2',
      type: 'recipe' as const,
      title: 'Perfect Cappuccino',
      timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: 'act_3',
      type: 'achievement' as const,
      title: 'First Brew Master',
      timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    },
  ];
  return activities;
}

function generateRecommendations(skillLevel: string, journeyStage: string) {
  return generateMockRecommendations(skillLevel, journeyStage, 5);
}

function calculatePriorityScore(
  rec: any,
  skillLevel: string,
  journeyStage: string
): number {
  let score = rec.confidence;

  // Boost score based on skill level match
  if (skillLevel === 'beginner' && rec.difficulty === 'easy') score += 0.2;
  if (skillLevel === 'expert' && rec.difficulty === 'hard') score += 0.2;

  // Boost score based on journey stage
  if (journeyStage === 'onboarding' && rec.type === 'lesson') score += 0.1;
  if (journeyStage === 'practicing' && rec.type === 'recipe') score += 0.1;

  return Math.min(1.0, score);
}

function generatePriorityReasons(
  rec: any,
  skillLevel: string,
  journeyStage: string
): string[] {
  const reasons = [];

  if (rec.confidence > 0.8) reasons.push('High confidence match');
  if (skillLevel === 'beginner' && rec.difficultyLevel === 'beginner')
    reasons.push('Perfect for beginners');
  if (journeyStage === 'onboarding' && rec.contentType === 'lesson')
    reasons.push('Great for getting started');
  if (rec.rating > 4.5) reasons.push('Highly rated by community');

  return reasons;
}

function getMotivationalMessage(
  skillLevel: string,
  journeyStage: string
): string {
  const messages = {
    beginner: {
      onboarding: "You're taking the first step on an amazing coffee journey!",
      learning: 'Every expert was once a beginner. Keep learning!',
      practicing: "Practice makes perfect. You're doing great!",
      mastering: "You're becoming a true coffee enthusiast!",
    },
    intermediate: {
      onboarding: 'Ready to level up your coffee skills?',
      learning: "You're building a solid foundation. Keep going!",
      practicing: 'Your skills are growing with each practice session!',
      mastering: "You're developing real expertise!",
    },
    advanced: {
      onboarding: 'Time to master advanced techniques!',
      learning: "You're exploring the deeper aspects of coffee!",
      practicing: 'Your advanced skills are impressive!',
      mastering: "You're becoming a coffee expert!",
    },
    expert: {
      onboarding: 'Welcome to the expert level!',
      learning: "You're pushing the boundaries of coffee knowledge!",
      practicing: 'Your expertise is inspiring others!',
      mastering: "You're a true coffee master!",
    },
  };

  return (
    messages[skillLevel as keyof typeof messages]?.[
      journeyStage as keyof (typeof messages)[keyof typeof messages]
    ] || 'Keep up the great work!'
  );
}

function getAdaptationSuggestions(
  skillLevel: string,
  journeyStage: string
): Array<{ id: string; title: string; description: string; priority: number }> {
  const suggestions = {
    beginner: [
      {
        id: 'sug_1',
        title: 'Start with basics',
        description: 'Focus on fundamental brewing methods first',
        priority: 0.9,
      },
      {
        id: 'sug_2',
        title: 'Practice regularly',
        description: 'Consistent practice will build your skills',
        priority: 0.8,
      },
    ],
    intermediate: [
      {
        id: 'sug_3',
        title: 'Try new methods',
        description: 'Experiment with different brewing techniques',
        priority: 0.8,
      },
      {
        id: 'sug_4',
        title: 'Learn latte art',
        description: 'Add visual appeal to your coffee creations',
        priority: 0.7,
      },
    ],
    advanced: [
      {
        id: 'sug_5',
        title: 'Master roasting',
        description: 'Understand how roasting affects flavor',
        priority: 0.9,
      },
      {
        id: 'sug_6',
        title: 'Create recipes',
        description: 'Develop your own unique coffee recipes',
        priority: 0.8,
      },
    ],
    expert: [
      {
        id: 'sug_7',
        title: 'Mentor others',
        description: 'Share your knowledge with beginners',
        priority: 0.9,
      },
      {
        id: 'sug_8',
        title: 'Innovate',
        description: 'Push the boundaries of coffee science',
        priority: 0.8,
      },
    ],
  };

  return suggestions[skillLevel as keyof typeof suggestions] || [];
}

function getUpcomingMilestones(
  skillLevel: string,
  journeyStage: string
): Array<{
  id: string;
  title: string;
  description: string;
  progress: number;
  target: number;
}> {
  const milestones = {
    beginner: [
      {
        id: 'mil_1',
        title: 'Complete 5 lessons',
        description: 'Finish your first set of lessons',
        progress: 3,
        target: 5,
      },
      {
        id: 'mil_2',
        title: 'Master basic brewing',
        description: 'Learn 3 fundamental brewing methods',
        progress: 1,
        target: 3,
      },
    ],
    intermediate: [
      {
        id: 'mil_3',
        title: 'Try 10 recipes',
        description: 'Experiment with different coffee recipes',
        progress: 6,
        target: 10,
      },
      {
        id: 'mil_4',
        title: 'Learn latte art',
        description: 'Master basic latte art techniques',
        progress: 0,
        target: 1,
      },
    ],
    advanced: [
      {
        id: 'mil_5',
        title: 'Create 5 recipes',
        description: 'Develop your own coffee recipes',
        progress: 2,
        target: 5,
      },
      {
        id: 'mil_6',
        title: 'Master roasting',
        description: 'Understand coffee bean roasting',
        progress: 0,
        target: 1,
      },
    ],
    expert: [
      {
        id: 'mil_7',
        title: 'Mentor 3 people',
        description: 'Help others learn coffee skills',
        progress: 1,
        target: 3,
      },
      {
        id: 'mil_8',
        title: 'Innovate technique',
        description: 'Develop a new coffee technique',
        progress: 0,
        target: 1,
      },
    ],
  };

  return milestones[skillLevel as keyof typeof milestones] || [];
}

// Generate progress insights array
function generateProgressInsights(
  skillLevel: string,
  journeyStage: string
): Array<{
  id: string;
  type: 'achievement' | 'improvement' | 'challenge' | 'milestone';
  title: string;
  description: string;
  metric: string;
  value: number;
  change: number;
  trend: 'up' | 'down' | 'stable';
  aiGenerated: boolean;
}> {
  const insights = [];

  // Add achievement insights
  if (skillLevel === 'beginner') {
    insights.push({
      id: `insight_${Date.now()}_1`,
      type: 'achievement' as const,
      title: 'First Steps Complete!',
      description: "You've completed your first coffee lesson. Great start!",
      metric: 'lessons_completed',
      value: 1,
      change: 1,
      trend: 'up' as const,
      aiGenerated: true,
    });
  }

  // Add improvement insights
  insights.push({
    id: `insight_${Date.now()}_2`,
    type: 'improvement' as const,
    title: 'Consistency is Key',
    description:
      'Your brewing technique is improving with each practice session.',
    metric: 'technique_score',
    value:
      skillLevel === 'beginner' ? 65 : skillLevel === 'intermediate' ? 78 : 85,
    change: 5,
    trend: 'up' as const,
    aiGenerated: true,
  });

  // Add challenge insights
  if (skillLevel === 'intermediate' || skillLevel === 'advanced') {
    insights.push({
      id: `insight_${Date.now()}_3`,
      type: 'challenge' as const,
      title: 'Ready for Advanced Techniques',
      description:
        'Try mastering latte art to take your skills to the next level.',
      metric: 'skill_level',
      value: skillLevel === 'intermediate' ? 2 : 3,
      change: 0,
      trend: 'stable' as const,
      aiGenerated: true,
    });
  }

  // Add milestone insights
  insights.push({
    id: `insight_${Date.now()}_4`,
    type: 'milestone' as const,
    title: 'Streak Milestone',
    description: `You're on a ${getCurrentStreak(skillLevel)}-day learning streak!`,
    metric: 'learning_streak',
    value: getCurrentStreak(skillLevel),
    change: 1,
    trend: 'up' as const,
    aiGenerated: false,
  });

  return insights;
}

// Generate streak encouragement message
function getStreakEncouragement(skillLevel: string): string {
  const encouragements = {
    beginner: "You're building great habits! Keep it up!",
    intermediate: 'Your consistency is paying off!',
    advanced: "You're becoming a true coffee expert!",
    expert: 'Your dedication is inspiring others!',
  };

  return (
    encouragements[skillLevel as keyof typeof encouragements] ||
    'Keep up the great work!'
  );
}
